export interface StaticCharacterFallbackOptions {
  signal?: AbortSignal;
  canvasWidth?: number;
  canvasHeight?: number;
}

/**
 * Draws one complete, non-animated character image when no accepted schema-3
 * package can be bound. This is a page-level compatibility fallback, never a
 * selector, frame, mouth, eye, pose, or expression fallback.
 */
export async function drawStaticCharacterFallback(
  canvas: HTMLCanvasElement,
  sourceUrl: string,
  options: StaticCharacterFallbackOptions = {},
): Promise<void> {
  if (!sourceUrl.startsWith("/") || sourceUrl.startsWith("//")) {
    throw new TypeError("A same-origin absolute static character path is required.");
  }
  throwIfAborted(options.signal);

  const image = new Image();
  image.decoding = "async";
  await new Promise<void>((resolve, reject) => {
    const cleanup = () => options.signal?.removeEventListener("abort", onAbort);
    const onAbort = () => {
      cleanup();
      image.src = "";
      reject(abortReason(options.signal));
    };
    image.onload = () => {
      cleanup();
      resolve();
    };
    image.onerror = () => {
      cleanup();
      reject(new Error(`Static character fallback did not load: ${sourceUrl}`));
    };
    options.signal?.addEventListener("abort", onAbort, { once: true });
    image.src = sourceUrl;
  });

  throwIfAborted(options.signal);
  if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
    throw new Error("Static character fallback has invalid dimensions.");
  }
  const canvasWidth = options.canvasWidth ?? image.naturalWidth;
  const canvasHeight = options.canvasHeight ?? image.naturalHeight;
  if (!Number.isSafeInteger(canvasWidth) || !Number.isSafeInteger(canvasHeight) ||
      canvasWidth <= 0 || canvasHeight <= 0) {
    throw new TypeError("Static character fallback canvas dimensions must be positive integers.");
  }
  if (image.naturalWidth > canvasWidth || image.naturalHeight > canvasHeight) {
    throw new Error("Static character fallback cannot be scaled down to fit its canvas.");
  }
  const context = canvas.getContext("2d", { alpha: true });
  if (!context) throw new Error("A 2D canvas is required for the static character fallback.");

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  canvas.style.imageRendering = "pixelated";
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, canvas.width, canvas.height);
  const offsetX = Math.floor((canvasWidth - image.naturalWidth) / 2);
  const offsetY = Math.floor((canvasHeight - image.naturalHeight) / 2);
  context.drawImage(image, offsetX, offsetY);
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw abortReason(signal);
}

function abortReason(signal: AbortSignal | undefined): unknown {
  return signal?.reason ?? new DOMException("Static character fallback was cancelled.", "AbortError");
}
